import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { auth as authApi, setTokens, clearTokens, getRefreshToken, type LoginResponse } from '../lib/api';

interface User { id: string; email: string; role: string; }
interface Tenant { id: string; name: string; plan: string; subscription_status: string; tenant_type?: string; }

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, tenantName: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  const handleLoginResponse = useCallback((data: LoginResponse) => {
    setTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    setTenant(data.tenant);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await authApi.login(email, password);
    handleLoginResponse(data);
  }, [handleLoginResponse]);

  const register = useCallback(async (email: string, password: string, tenantName: string) => {
    const data = await authApi.register(email, password, tenantName);
    handleLoginResponse(data);
  }, [handleLoginResponse]);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    clearTokens();
    setUser(null);
    setTenant(null);
  }, []);

  useEffect(() => {
    const rt = getRefreshToken();
    if (!rt) { setLoading(false); return; }
    authApi.me()
      .then(data => {
        if (data.user) setUser(data.user);
        if (data.tenant) setTenant(data.tenant);
      })
      .catch(() => { clearTokens(); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <AuthCtx.Provider value={{ user, tenant, isAuthenticated: !!user, loading, login, register, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}
